import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientsApi, Client } from '../api/clients';
import { photoIdApi } from '../api/photoId';
import './ClientDetail.css';

const ClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [claims, setClaims] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Client>>({});
  const [saving, setSaving] = useState(false);
  const [sendingIdRequest, setSendingIdRequest] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchClientData();
    }
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch client details
      const clientData = await clientsApi.getClient(clientId!);
      setClient(clientData);
      setClaims(clientData.claims || []);
      
      // Initialize edit form
      setEditForm({
        firstName: clientData.firstName,
        lastName: clientData.lastName,
        email: clientData.email,
        phoneNumber: clientData.phoneNumber,
        addressLine1: clientData.addressLine1,
        addressLine2: clientData.addressLine2,
        city: clientData.city,
        postcode: clientData.postcode,
        dateOfBirth: clientData.dateOfBirth,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to fetch client details');
      console.error('Error fetching client:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const updatedClient = await clientsApi.updateClient(clientId!, editForm);
      setClient(updatedClient);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update client');
      console.error('Error updating client:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    if (client) {
      setEditForm({
        firstName: client.firstName,
        lastName: client.lastName,
        email: client.email,
        phoneNumber: client.phoneNumber,
        addressLine1: client.addressLine1,
        addressLine2: client.addressLine2,
        city: client.city,
        postcode: client.postcode,
        dateOfBirth: client.dateOfBirth,
      });
    }
  };

  const handleSendIdRequest = async () => {
    try {
      setSendingIdRequest(true);
      setError(null);
      
      const result = await photoIdApi.sendIdRequest([clientId!]);
      
      if (result.sent > 0) {
        // Refresh client data to show updated status
        await fetchClientData();
        alert('ID request sent successfully');
      } else if (result.errors.length > 0) {
        setError(`Failed to send ID request: ${result.errors[0].error}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send ID request');
      console.error('Error sending ID request:', err);
    } finally {
      setSendingIdRequest(false);
    }
  };

  const getVerificationStatus = () => {
    if (!client) return null;
    
    switch (client.idVerificationStatus) {
      case 'verified':
        return (
          <div className="status-badge status-verified">
            <span className="status-icon">✓</span>
            ID Verified
            {client.idVerificationDate && (
              <span className="status-date">
                {new Date(client.idVerificationDate).toLocaleDateString()}
              </span>
            )}
          </div>
        );
      case 'pending':
        return (
          <div className="status-badge status-pending">
            <span className="status-icon">⏳</span>
            ID Verification Pending
          </div>
        );
      case 'rejected':
        return (
          <div className="status-badge status-rejected">
            <span className="status-icon">✗</span>
            ID Rejected
            <button
              className="btn btn-small btn-primary"
              onClick={handleSendIdRequest}
              disabled={sendingIdRequest}
            >
              Resend Request
            </button>
          </div>
        );
      default:
        if (client.idRequestSent) {
          return (
            <div className="status-badge status-requested">
              <span className="status-icon">📧</span>
              ID Request Sent
              {client.idRequestSentDate && (
                <span className="status-date">
                  {new Date(client.idRequestSentDate).toLocaleDateString()}
                </span>
              )}
              <button
                className="btn btn-small btn-secondary"
                onClick={handleSendIdRequest}
                disabled={sendingIdRequest}
              >
                Resend
              </button>
            </div>
          );
        }
        return (
          <div className="status-badge status-none">
            <span className="status-icon">-</span>
            No ID Verification
            <button
              className="btn btn-small btn-primary"
              onClick={handleSendIdRequest}
              disabled={sendingIdRequest}
            >
              Send ID Request
            </button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading client details...</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="error-container">
        <h2>Client not found</h2>
        <button className="btn btn-primary" onClick={() => navigate('/clients')}>
          Back to Clients
        </button>
      </div>
    );
  }

  return (
    <div className="client-detail-page">
      <div className="page-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/clients')}>
            ← Back to Clients
          </button>
          <h1>{client.firstName} {client.lastName}</h1>
        </div>
        <div className="header-actions">
          {!isEditing && (
            <button className="btn btn-secondary" onClick={() => setIsEditing(true)}>
              Edit Client
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="client-content">
        <div className="client-info-section">
          <div className="section-header">
            <h2>Client Information</h2>
            {isEditing && (
              <div className="edit-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  className="btn btn-text"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="info-grid">
            <div className="info-group">
              <label>First Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.firstName || ''}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.firstName}</p>
              )}
            </div>

            <div className="info-group">
              <label>Last Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.lastName || ''}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.lastName}</p>
              )}
            </div>

            <div className="info-group">
              <label>Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.email}</p>
              )}
            </div>

            <div className="info-group">
              <label>Phone Number</label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editForm.phoneNumber || ''}
                  onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.phoneNumber || '-'}</p>
              )}
            </div>

            <div className="info-group">
              <label>Date of Birth</label>
              {isEditing ? (
                <input
                  type="date"
                  value={editForm.dateOfBirth || ''}
                  onChange={(e) => setEditForm({ ...editForm, dateOfBirth: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.dateOfBirth || '-'}</p>
              )}
            </div>

            <div className="info-group">
              <label>Address Line 1</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.addressLine1 || ''}
                  onChange={(e) => setEditForm({ ...editForm, addressLine1: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.addressLine1 || '-'}</p>
              )}
            </div>

            <div className="info-group">
              <label>Address Line 2</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.addressLine2 || ''}
                  onChange={(e) => setEditForm({ ...editForm, addressLine2: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.addressLine2 || '-'}</p>
              )}
            </div>

            <div className="info-group">
              <label>City</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.city || ''}
                  onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.city || '-'}</p>
              )}
            </div>

            <div className="info-group">
              <label>Postcode</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.postcode || ''}
                  onChange={(e) => setEditForm({ ...editForm, postcode: e.target.value })}
                  className="form-input"
                />
              ) : (
                <p>{client.postcode || '-'}</p>
              )}
            </div>
          </div>

          <div className="verification-section">
            <h3>ID Verification Status</h3>
            {getVerificationStatus()}
          </div>
        </div>

        <div className="claims-section">
          <div className="section-header">
            <h2>Associated Claims ({claims.length})</h2>
          </div>

          {claims.length > 0 ? (
            <div className="claims-table-container">
              <table className="claims-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Lender</th>
                    <th>Status</th>
                    <th>LOC Sent</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((claim) => (
                    <tr key={claim.id}>
                      <td>{claim.proclaimReference || claim.id}</td>
                      <td>{claim.craFinanceLender || '-'}</td>
                      <td>
                        <span className={`status-pill status-${claim.status?.toLowerCase()}`}>
                          {claim.status}
                        </span>
                      </td>
                      <td>
                        {claim.locSentDate
                          ? new Date(claim.locSentDate).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>
                        {claim.createdAt
                          ? new Date(claim.createdAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => navigate(`/claim-management?claimId=${claim.sourceClaimId || claim.id}`)}
                        >
                          View Claim
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-claims">
              <p>No claims associated with this client</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;